// Shared leak-detection logic used by both the per-well LeakDetector and the
// fleet-wide FleetLeaks summary, so both stay in exact agreement.

export const DEFAULT_DECLINE_PER_DAY = 0.0013; // ~0.13%/day natural decline

export function median(a) {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * @param series array of { t: Date, rate: number } sorted by time
 * @param opts.minFills minimum consecutive flagged fills to confirm a leak
 *   (default 2). A single low fill that recovers immediately is treated as
 *   noise, not a leak.
 * @param opts.requirePersist if true (default), reject events where the fill
 *   rate recovers to >=85% of its pre-event baseline within 2 fills after the
 *   event. A real (unrepaired) leak does not heal itself; a dip-and-recover is
 *   a temporary operational event (choke/shut-in/slow fill), not a leak.
 * @returns { rows, events } — rows annotated with expected/leak, events grouped
 */
export function detectLeaksFromSeries(series, { window, dropThresh, declinePerDay, minFills = 2, requirePersist = true }) {
  const rows = series.map((p, i) => {
    let expected = null;
    let leak = false;
    let dropPct = null;
    if (i >= window) {
      const prev = series.slice(Math.max(0, i - window), i).map(q => q.rate);
      const base = median(prev);
      const dtDays = Math.max(0, (p.t - series[i - 1].t) / 86400000);
      expected = base * (1 - declinePerDay * dtDays);
      if (expected > 0.05 && p.rate < expected * (1 - dropThresh)) {
        leak = true;
        dropPct = (100 * (expected - p.rate)) / expected;
      }
    }
    return {
      ...p,
      ms: p.t.getTime(),
      time: p.time,
      expected: expected != null ? +expected.toFixed(3) : null,
      leak,
      dropPct,
      leakRate: leak ? p.rate : null,
    };
  });

  const raw = [];
  let cur = null;
  for (const r of rows) {
    if (r.leak) {
      if (cur && (r.t - cur.lastT) / 86400000 <= 3) {
        cur.points.push(r);
        cur.lastT = r.t;
        cur.maxDrop = Math.max(cur.maxDrop, r.dropPct);
      } else {
        if (cur) raw.push(cur);
        cur = { start: r.time, startT: r.t, lastT: r.t, points: [r], maxDrop: r.dropPct };
      }
    }
  }
  if (cur) raw.push(cur);

  // Require persistence: a real leak stays suppressed for >= minFills
  // consecutive fills. Single-fill blips that recover are dropped as noise.
  let events = raw.filter(e => e.points.length >= minFills);

  // Reject events that recover on their own: find each event's index range in
  // rows, take the pre-event baseline, and drop the event if the fill rate
  // climbs back to >=85% of that baseline within 2 fills after it ends.
  if (requirePersist) {
    const idxByMs = new Map(rows.map((r, i) => [r.ms, i]));
    events = events.filter(e => {
      const firstI = idxByMs.get(e.points[0].ms);
      const lastI = idxByMs.get(e.points[e.points.length - 1].ms);
      const base = median(rows.slice(Math.max(0, firstI - window), firstI).map(r => r.rate));
      if (base <= 0) return true;
      const after = rows.slice(lastI + 1, lastI + 3).map(r => r.rate);
      const recovered = after.length > 0 && Math.max(...after) >= base * 0.85;
      return !recovered;
    });
  }

  // Un-flag rows that belonged to rejected (too-short / recovered) events so
  // the chart and counts stay consistent with the confirmed events.
  const kept = new Set(events.flatMap(e => e.points.map(p => p.ms)));
  for (const r of rows) {
    if (r.leak && !kept.has(r.ms)) {
      r.leak = false;
      r.leakRate = null;
    }
  }

  return { rows, events };
}
